/**
 * O modelo da coluna de fórmula — a parte que **não** depende do BlockSuite.
 *
 * ## Por que este arquivo é separado do `define.ts`
 *
 * `define.ts` chama `propertyType()` e lê `t.number.instance()` — os dois vêm de
 * `@blocksuite/affine/blocks/database`, que arrasta `lit` e DOM. Isso torna `define.ts`
 * intestável fora de um build do Affine.
 *
 * Mas quase nada do que `define.ts` **decide** precisa do BlockSuite: o schema do que fica
 * gravado, os defaults, a tabela formato → tipo lógico, o texto que a célula mostra. Tudo
 * isso mora aqui e roda nesta estação, junto com o avaliador.
 *
 * ⚠️ A linha divisória é deliberada, e é a mesma da regra **8.48.1**: **decisão nossa se
 * prova aqui; ligação com o lado deles se prova no campo.** O que sobrou no `define.ts` é
 * uma tabela de correspondência de seis linhas e uma chamada de fábrica — o mínimo que não
 * dá para provar sem o outro lado presente.
 */

import { comoTextoFormatado, FORMATOS, LOCALE_PADRAO, MOEDA_PADRAO } from './avaliador';
import type { FormatoDeSaida } from './avaliador';

/*
 * ⚠️ **Sem `zod` aqui, de propósito.**
 *
 * O schema `zod` é o contrato com o `PropertyConfig` do BlockSuite — serialização. Ele vive no
 * `define.ts` e é MONTADO a partir das declarações deste arquivo, para que a lista de formatos
 * e os limites tenham um lugar só (Regra #9).
 *
 * A razão prática é que `zod` não existe nesta estação (o `node_modules` do Affine só existe
 * no alvo provisionado), e um domínio que dependesse dele voltaria a só se testar depois de um
 * build de 16 minutos. A razão de desenho é melhor: *quais formatos existem* é decisão;
 * *como isso vira JSON validado* é detalhe.
 */

export const TIPO_DA_COLUNA_DE_FORMULA = 'tuninho-formula';

/** A configuração da coluna — o que fica gravado no documento. */
export interface FormulaPropertyData {
  /** A expressão, com as colunas já referenciadas por `propertyId`. */
  expressao: string;
  /** Como o resultado é exibido — e, por tabela, qual o tipo lógico da coluna. */
  formato: string;
  /** `undefined` = o default de cada formato (2 para moeda, 1 para percentual). */
  casas?: number;
  /**
   * Locale e moeda ficam GRAVADOS, não deduzidos do navegador.
   *
   * É o achado da F1: `formatNumber` do upstream usa `navigator.language`, e por isso um
   * `currencyBRL` acrescentado ao enum deles renderizaria `R$ 1,234.56` para quem tem
   * navegador `en-US`. Gravar significa que o mesmo documento mostra o mesmo número para
   * todo mundo — o mínimo que se espera de um relatório financeiro.
   */
  locale: string;
  moeda: string;
}

/** Os limites de `casas`, num lugar só — o `define.ts` os repassa ao `zod`. */
export const CASAS_MINIMO = 0;
export const CASAS_MAXIMO = 15;

export const dadosPadraoDaFormula = (): FormulaPropertyData => ({
  expressao: '',
  formato: 'numero',
  casas: undefined,
  locale: LOCALE_PADRAO,
  moeda: MOEDA_PADRAO,
});

/**
 * A configuração é válida? Devolve a lista de problemas — vazia quando está tudo certo.
 *
 * Existe ao lado do schema `zod` e não no lugar dele: o `zod` guarda o portão do documento
 * (o que entra em `propertyData`), esta função é o que o EDITOR usa para avisar enquanto a
 * pessoa digita, e o que o teste desta estação consegue exercitar.
 */
export function problemasDaConfiguracao(dados: Partial<FormulaPropertyData>): string[] {
  const problemas: string[] = [];
  if (dados.formato !== undefined && !FORMATOS.includes(dados.formato as FormatoDeSaida)) {
    problemas.push(`formato "${dados.formato}" não existe — use ${FORMATOS.join(', ')}`);
  }
  if (dados.casas !== undefined) {
    if (!Number.isInteger(dados.casas)) {
      problemas.push('casas decimais precisa ser inteiro');
    } else if (dados.casas < CASAS_MINIMO || dados.casas > CASAS_MAXIMO) {
      problemas.push(`casas decimais entre ${CASAS_MINIMO} e ${CASAS_MAXIMO}`);
    }
  }
  if (dados.locale !== undefined && !dados.locale.trim()) {
    problemas.push('locale não pode ser vazio');
  }
  return problemas;
}

/**
 * O valor bruto de uma célula de fórmula.
 *
 * `null` quando não há o que mostrar (expressão vazia ou resultado vazio). O erro viaja como
 * OBJETO para que a célula possa mostrar o rótulo curto e o motivo inteiro no tooltip — um
 * erro reduzido a string perderia o código, e o código é o que os testes afirmam.
 */
export interface ErroNaCelula {
  __erroDeFormula: true;
  codigo: string;
  mensagem: string;
  rotulo: string;
}

export type FormulaRawValue = number | string | boolean | ErroNaCelula | null;

export const ehErroDeFormula = (
  v: unknown
): v is { __erroDeFormula: true; codigo: string; mensagem: string; rotulo: string } =>
  typeof v === 'object' && v !== null && '__erroDeFormula' in v;

/**
 * Que tipo lógico o formato escolhido produz (T2.6 / decisão D-C).
 *
 * Devolve o **nome** do tipo, não a instância — a instância exige `t` do BlockSuite, e o
 * `define.ts` faz essa tradução numa tabela de seis linhas. O que se decide aqui é a
 * correspondência, que é onde mora a consequência: é ela que faz `sum` casar numa coluna de
 * moeda e não casar numa de texto, porque a estatística casa por `TypeInstance`
 * (`core/statistics/`).
 *
 * ⚠️ **`erro` não muda o tipo da coluna.** Uma linha com erro não pode fazer a coluna inteira
 * deixar de ser numérica — senão a soma do rodapé sumiria porque um lançamento tem divisão
 * por zero, e o usuário procuraria o problema no lugar errado.
 */
export type NomeDeTipoLogico = 'number' | 'date' | 'boolean' | 'string';

export function tipoLogicoDoFormato(formato: string | undefined): NomeDeTipoLogico {
  switch (formato) {
    case 'moeda':
    case 'percentual':
    case 'numero':
      return 'number';
    case 'data':
      // O Affine já guarda data como número (`t.date` é `defineDataType('Date', number)`).
      // Não se inventa um tipo aqui — seria a segunda verdade da Regra #9.
      return 'date';
    case 'booleano':
      return 'boolean';
    case 'texto':
      return 'string';
    default:
      // Formato ausente ou desconhecido cai em número, que é o default da coluna. Cair em
      // `string` faria uma coluna com config corrompida perder a soma sem dizer por quê.
      return 'number';
  }
}

/**
 * O texto que a célula mostra — usado pelo `toString` e pelo render.
 *
 * ⚠️ A formatação mora AQUI e não no cálculo, e a divisão importa: `spacialValueGet` devolve
 * o valor **cru** (um número), e é ele que `toJson` entrega à estatística de rodapé. Se o
 * cálculo já devolvesse `"R$ 1.234,56"`, a soma da coluna somaria texto — e em JavaScript
 * `"R$ 1.234,56" + "R$ 10,00"` não falha: concatena.
 */
export function textoDaCelula(
  value: FormulaRawValue,
  data: FormulaPropertyData | undefined
): string {
  if (ehErroDeFormula(value)) return value.rotulo;
  if (value === null || value === undefined) return '';
  return comoTextoFormatado(
    value as never,
    (data?.formato ?? 'numero') as FormatoDeSaida,
    { locale: data?.locale, moeda: data?.moeda, casas: data?.casas }
  );
}

/** Erro NÃO é vazio: `isEmpty` verdadeiro faria a linha sumir de um filtro "não vazio" e
 *  esconderia exatamente a linha que precisa de conserto. */
export function celulaVazia(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}
